type TranslationError = {
	key: string;
	defaultMessage: string;
	params?: Record<string, string | number>;
};

export const MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

export const MAX_DESCRIPTION_CHARACTERS = 50;

const MAX_FILENAME_CHARACTERS = 250;

export const ALLOWED_FILE_EXTENSIONS: ReadonlyArray<string> = [
	'pdf',
	'doc',
	'docx',
	'jpeg',
	'jpg',
	'png',
	'tif',
	'tiff',
	'bmp',
	'xls',
	'xlsx',
	'csv',
	'heic',
];

const FORBIDDEN_FILENAME_CHARS = [
	'<',
	'>',
	':',
	'"',
	'/',
	'|',
	'?',
	'*',
] as const;

export const DROPZONE_ACCEPT_MAP: Readonly<Record<string, string[]>> = {
	'application/pdf': ['.pdf'],
	'application/msword': ['.doc'],
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
		'.docx',
	],
	'image/jpeg': ['.jpeg', '.jpg'],
	'image/png': ['.png'],
	'image/tiff': ['.tif', '.tiff'],
	'image/bmp': ['.bmp'],
	'application/vnd.ms-excel': ['.xls'],
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
		'.xlsx',
	],
	'text/csv': ['.csv'],
	'image/heic': ['.heic'],
};

export const FORBIDDEN_FILENAME_CHARACTERS_REGEX = /[<>:"/|?*]/;

export function getFileExtensionFromName(fileName: string): string {
	const lastDotIndex = fileName.lastIndexOf('.');
	return lastDotIndex > -1
		? fileName.slice(lastDotIndex + 1).toLowerCase()
		: '';
}

/**
 * Sanitizes filename by replacing spaces and extra dots (except the extension dot) with underscores.
 * Example: "my file.name.pdf" -> "my_file_name.pdf"
 */
export function sanitizeFileName(fileName: string): string {
	const lastDotIndex = fileName.lastIndexOf('.');
	if (lastDotIndex <= 0) {
		// No extension or dot at the start, replace all dots and spaces
		return fileName.replace(/[\s.]+/g, '_');
	}
	const baseName = fileName.slice(0, lastDotIndex);
	const extension = fileName.slice(lastDotIndex);
	return baseName.replace(/[\s.]+/g, '_') + extension;
}

export const validateDocumentFileName = (
	rawFileName?: string,
): TranslationError | null => {
	if (!rawFileName) {
		return {
			key: 'pages.shipment-detail.modal.add-document-name-required',
			defaultMessage: 'File name is required.',
		};
	}

	const trimmedFileName = rawFileName.trim();
	if (!trimmedFileName) {
		return {
			key: 'pages.shipment-detail.modal.add-document-name-blank',
			defaultMessage: 'File name cannot be blank.',
		};
	}

	if (trimmedFileName.length > MAX_FILENAME_CHARACTERS) {
		return {
			key: 'pages.shipment-detail.modal.add-document-name-too-long',
			defaultMessage:
				'File name must be {{max}} characters or fewer (current: {{current}}).',
			params: {
				max: MAX_FILENAME_CHARACTERS,
				current: trimmedFileName.length,
			},
		};
	}

	if (FORBIDDEN_FILENAME_CHARACTERS_REGEX.test(trimmedFileName)) {
		return {
			key: 'pages.shipment-detail.modal.add-document-name-forbidden-chars',
			defaultMessage: 'File name contains forbidden characters: {{chars}}',
			params: { chars: FORBIDDEN_FILENAME_CHARS.join(' ') },
		};
	}

	return null;
};
